import React, { useState, useEffect } from "react";
import { CalendarIcon, CheckIcon, DocumentIcon, GridIcon, SyncIcon } from "../../icons";
import { useGrantsStats } from "../../../hooks/useGrantsStats";

/**
 * Variant determines which stats to show:
 * - 'calls': For grants/calls page - sources, discovered articles, checks (no open calls to avoid confusion)
 * - 'default': Original behavior with open calls, sources, articles
 */
type StatsBarVariant = "default" | "calls";

interface StatsBarProps {
  /**
   * Which variant of stats to show.
   * @default "default"
   */
  variant?: StatsBarVariant;
  /**
   * Whether the stats bar can be dismissed (hidden by user).
   * When true, shows dismiss button and persists state in localStorage.
   */
  dismissable?: boolean;
  /**
   * localStorage key for dismiss state.
   * @default "grants-stats-dismissed"
   */
  storageKey?: string;
  /**
   * Additional CSS classes for the container.
   */
  className?: string;
}

/**
 * Stats bar component displaying key platform statistics.
 *
 * Variants:
 * - 'default': open calls, monitored sources, new articles (24h)
 * - 'calls': monitored sources, discovered articles (24h), source checks (24h)
 *
 * Features:
 * - Responsive: row on desktop, column on mobile
 * - Graceful degradation: hides on API error
 * - Loading skeleton state
 * - Tooltips explaining each statistic
 * - Optional dismiss functionality with localStorage persistence
 */
const StatsBar: React.FC<StatsBarProps> = ({
  variant = "default",
  dismissable = false,
  storageKey = "grants-stats-dismissed",
  className = "",
}) => {
  const { data, isLoading, error } = useGrantsStats();
  const [isDismissed, setIsDismissed] = useState(false);

  // Load dismiss state from localStorage
  useEffect(() => {
    if (dismissable) {
      try {
        const dismissed = localStorage.getItem(storageKey);
        setIsDismissed(dismissed === "true");
      } catch (e) {
        console.warn("localStorage read error:", e);
      }
    }
  }, [dismissable, storageKey]);

  // Handle dismiss
  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      localStorage.setItem(storageKey, "true");
    } catch (e) {
      console.warn("localStorage quota exceeded:", e);
    }
  };

  // Handle show stats
  const handleShow = () => {
    setIsDismissed(false);
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn("localStorage remove error:", e);
    }
  };

  // Hide on error (graceful degradation)
  if (error) {
    return null;
  }

  // Show "Pokaż statystyki" link when dismissed
  if (dismissable && isDismissed) {
    return (
      <div className={`text-center ${className}`}>
        <button
          onClick={handleShow}
          className="text-sm text-neutral-500 hover:text-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          aria-label="Pokaż statystyki"
        >
          Pokaż statystyki
        </button>
      </div>
    );
  }

  // Loading skeleton
  const skeletonCount = variant === "calls" ? 4 : 3;
  if (isLoading) {
    return (
      <div
        className={`bg-neutral-50 border border-neutral-200 rounded-lg p-4 ${className}`}
        role="status"
        aria-label="Ładowanie statystyk"
      >
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          {Array.from({ length: skeletonCount }, (_, i) => i + 1).map((i) => (
            <div key={i} className="flex items-center gap-2 animate-pulse" data-testid="skeleton">
              <div className="w-5 h-5 bg-neutral-200 rounded" />
              <div className="w-24 h-4 bg-neutral-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Get activity stats with allTime fallback for empty state
  const activity = data.activity;
  const articlesDiscovered = activity
    ? (activity.last24h.articlesDiscovered > 0
        ? activity.last24h.articlesDiscovered
        : activity.allTime.articlesDiscovered)
    : 0;
  const checksPerformed = activity
    ? (activity.last24h.checksPerformed > 0
        ? activity.last24h.checksPerformed
        : activity.allTime.checksPerformed)
    : 0;

  // Stats for 'calls' variant - activity-focused
  const callsStats = [
    {
      icon: GridIcon,
      value: data.totalSources,
      label: "monitorowanych źródeł",
      tooltip: "Liczba stron internetowych (urzędów pracy, operatorów BUR) monitorowanych przez system",
      ariaLabel: `${data.totalSources.toLocaleString('pl-PL')} monitorowanych źródeł`,
    },
    {
      icon: SyncIcon,
      value: checksPerformed,
      label: "sprawdzeń źródeł (24h)",
      tooltip: "Tyle razy sprawdziliśmy wszystkie dostępne źródła – artykuły, pliki PDF i obrazy – aby upewnić się, że żadna zmiana nie umknęła naszej uwadze, nawet gdy operator aktualizuje treść bez publikowania nowego artykułu",
      ariaLabel: `${checksPerformed.toLocaleString('pl-PL')} sprawdzeń źródeł w ostatnich 24 godzinach`,
    },
    {
      icon: DocumentIcon,
      value: articlesDiscovered,
      label: "znalezionych ogłoszeń (24h)",
      tooltip: "Nowe informacje opublikowane przez operatorów – każde ogłoszenie to artykuł, który normalnie trzeba by przeczytać samodzielnie, żeby sprawdzić, czy pojawiły się nowe nabory",
      ariaLabel: `${articlesDiscovered.toLocaleString('pl-PL')} znalezionych ogłoszeń w ostatnich 24 godzinach`,
    },
    {
      icon: CheckIcon,
      value: data.callsUpdatedLast7d ?? 0,
      label: "zaktualizowanych naborów (7d)",
      tooltip: "Nabory dodane lub zaktualizowane w ciągu ostatnich 7 dni – wdrożone rekomendacje systemu i ręczne aktualizacje naszego zespołu, abyś zawsze miał najbardziej aktualne informacje",
      ariaLabel: `${(data.callsUpdatedLast7d ?? 0).toLocaleString('pl-PL')} zaktualizowanych naborów w ostatnich 7 dniach`,
    },
  ];

  // Stats for 'default' variant - original behavior
  const defaultStats = [
    {
      icon: CalendarIcon,
      value: data.totalOpenCalls,
      label: "otwartych naborów",
      tooltip: "Nabory aktualnie przyjmujące wnioski",
      ariaLabel: `${data.totalOpenCalls.toLocaleString('pl-PL')} otwartych naborów`,
    },
    {
      icon: GridIcon,
      value: data.totalSources,
      label: "monitorowanych źródeł",
      tooltip: "Liczba stron internetowych (urzędów pracy, operatorów BUR) monitorowanych przez system",
      ariaLabel: `${data.totalSources.toLocaleString('pl-PL')} monitorowanych źródeł`,
    },
    {
      icon: DocumentIcon,
      value: data.articlesLast24h,
      label: "nowych (24h)",
      tooltip: "Nowe artykuły dodane do systemu w ostatnich 24 godzinach",
      ariaLabel: `${data.articlesLast24h.toLocaleString('pl-PL')} nowych artykułów w ostatnich 24 godzinach`,
    },
  ];

  const stats = variant === "calls" ? callsStats : defaultStats;

  return (
    <div
      className={`bg-neutral-50 border border-neutral-200 rounded-lg p-4 relative ${className}`}
      role="region"
      aria-label="Statystyki systemu"
    >
      {/* Dismiss button */}
      {dismissable && (
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-neutral-400 hover:text-neutral-600 transition-colors"
          aria-label="Ukryj statystyki"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}

      {/* Stats items */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="flex items-center gap-2 text-neutral-700 cursor-help"
            title={stat.tooltip}
            aria-label={stat.ariaLabel}
          >
            <stat.icon className="w-5 h-5 text-primary-600" aria-hidden={true} />
            <span className="font-semibold text-primary-700">{stat.value.toLocaleString('pl-PL')}</span>
            <span className="text-sm text-neutral-600">{stat.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsBar;
